import React from 'react';
import {RichText} from 'prismic-reactjs';
import PropTypes from 'prop-types';

import {
    Box,
    ButtonGroup,
    Container,
    Grid,
    GridItem,
    Heading,
} from '@spothero/ui';

import {
    bodySerializer,
    getSliceThemeVariables,
    ignoreEmptyRichTextObjects,
} from 'cms/utils';
import {
    IMAGE_PROP_TYPES,
    CTA_BUTTON_PROP_TYPES,
    RICHTEXT_PROP_TYPES,
    SLICE_PROP_TYPES,
} from 'cms/proptypes';
import Image from 'common/image';
import ExtendedButton from 'cms/components/extended-button';

const FiftyFiftySplitSection = ({slice}) => {
    const {
        primary: {
            section_id: sectionId,
            '50_50_slice_title': title,
            '50_50_slice_theme': theme,
            '50_50_slice_rich_text': richText,
            '50_50_slice_feature_image': featureImage,
            '50_50_slice_background_image': backgroundImage,
            feature_image_desktop_position_right: imageRight,
            feature_image_mobile_position_top: imageTopMobile,
        },
        items,
    } = slice;

    const {
        textColor,
        backgroundColor,
        primaryButtonVariant,
        secondaryButtonVariant,
        linkVariant,
    } = getSliceThemeVariables(theme);

    // Change the display order of text block and image blocks through CSS
    let textBlockOrderTablet;
    let imageOrderTablet;
    let textBlockOrderMobile;
    let imageOrderMobile;

    if (imageRight) {
        textBlockOrderTablet = 1;
        imageOrderTablet = 2;
    } else {
        textBlockOrderTablet = 2;
        imageOrderTablet = 1;
    }

    if (imageTopMobile) {
        textBlockOrderMobile = 2;
        imageOrderMobile = 1;
    } else {
        textBlockOrderMobile = 1;
        imageOrderMobile = 2;
    }

    const populatedRichText = ignoreEmptyRichTextObjects(richText);

    const ctaButtons = items
        .slice(0, 2)
        .filter(button => button.cta_text)
        .map((button, i) =>
            ExtendedButton({
                buttonText: button.cta_text,
                inPageAnchor: button.in_page_anchor,
                key: i,
                link: button.cta_external_link.url,
                linkTarget: button.cta_external_link.target,
                variant:
                    i === 0 ? primaryButtonVariant : secondaryButtonVariant,
                marginY: 2,
            })
        );

    return (
        <Box
            as="section"
            id={sectionId}
            data-testid="FiftyFiftySplitSection"
            paddingTop={{base: 8, tablet: 16}}
            paddingBottom={{base: 8, tablet: 16}}
            backgroundColor={backgroundColor}
            backgroundImage={
                backgroundImage.url ? `url(${backgroundImage?.url})` : null
            }
            backgroundSize="cover"
            backgroundPosition="center"
            color={textColor}
        >
            <Container>
                <Box textAlign={{base: 'center', tablet: 'left'}}>
                    <Grid>
                        <GridItem
                            order={{
                                base: textBlockOrderMobile,
                                tablet: textBlockOrderTablet,
                            }}
                            colStart={{desktop: imageRight ? null : 8}}
                            colSpan={{base: 12, tablet: 6, desktop: 5}}
                            alignSelf="center"
                        >
                            {Boolean(title) && (
                                <Heading
                                    as="h2"
                                    marginBottom={6}
                                    data-testid="FiftyFiftySplitSection-Title"
                                >
                                    {title}
                                </Heading>
                            )}
                            {Boolean(populatedRichText.length) && (
                                <RichText
                                    render={populatedRichText}
                                    htmlSerializer={bodySerializer({
                                        linkVariant,
                                    })}
                                />
                            )}
                            {Boolean(ctaButtons.length) && (
                                <ButtonGroup
                                    alignContent="space-between"
                                    justifyContent="center"
                                    flexWrap={{
                                        base: 'wrap',
                                        mobileXL: 'nowrap',
                                    }}
                                    spacing={{base: 2, tablet: 4}}
                                >
                                    {ctaButtons}
                                </ButtonGroup>
                            )}
                        </GridItem>
                        <GridItem
                            order={{
                                base: imageOrderMobile,
                                tablet: imageOrderTablet,
                            }}
                            colSpan={{base: 12, tablet: 6}}
                            colStart={{desktop: imageRight ? 7 : null}}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                '.Image': {
                                    maxHeight: '600px',
                                    width: 'auto',
                                    height: 'auto',
                                },
                            }}
                        >
                            {featureImage?.url && (
                                <Image
                                    src={featureImage?.url}
                                    alt={featureImage?.alt || ''}
                                    lazyLoad
                                    showLoader={false}
                                />
                            )}
                        </GridItem>
                    </Grid>
                </Box>
            </Container>
        </Box>
    );
};

/* eslint-disable camelcase */
FiftyFiftySplitSection.propTypes = {
    slice: PropTypes.shape({
        ...SLICE_PROP_TYPES,
        primary: PropTypes.shape({
            '50_50_slice_background_image': IMAGE_PROP_TYPES,
            '50_50_slice_feature_image': IMAGE_PROP_TYPES,
            '50_50_slice_theme': PropTypes.string.isRequired,
            '50_50_slice_rich_text': RICHTEXT_PROP_TYPES,
            '50_50_slice_title': PropTypes.string,
            feature_image_desktop_position_right: PropTypes.bool,
            feature_image_mobile_position_top: PropTypes.bool,
            section_id: PropTypes.string,
        }),
        items: PropTypes.arrayOf(CTA_BUTTON_PROP_TYPES),
    }),
};
/* eslint-enable camelcase */

export default FiftyFiftySplitSection;
